/**
 * Hook optimisé pour les actions sur les crews
 * N'utilise PAS useCRUD, donc ne charge pas toutes les données
 * Utilisé dans ContactDetailPage pour éviter les requêtes inutiles
 */

import { useCallback } from 'react';
import { useUsers } from '../contexts/UsersContext';
import { isOver28AtEventDate } from '../lib/age-utils';
import { monitorQuery } from '../lib/supabase-monitor';
import { logCrewMemberAdded, logCrewMemberRemoved, logEntityUpdate } from '../lib/timeline-logger';
import type { Crew, CrewMemberRole } from '../types/crew';
import { useAuth } from './useAuth';
import { useSupabase } from './useSupabase';

/** 'partial' : l'équipage est enregistré mais la dérogation n'a pas pu être appliquée aux membres. */
export type UpdateCrewResult = 'saved' | 'partial' | 'failed';

export function useCrewActions() {
  const supabase = useSupabase();
  const { user } = useAuth();
  const { usersMap } = useUsers();

  /**
   * Récupérer un crew par ID avec toutes ses relations
   */
  const getCrewById = useCallback(
    async (id: string): Promise<Crew | null> => {
      try {
        const { data, error } = await monitorQuery(
          'crews',
          'select',
          `id=${id}`,
          (() =>
            supabase
              .from('crews')
              .select(
                `
            *,
            product:products(id, name, start_date, end_date),
            vehicle:vehicles(id, registration),
            association:associations(id, name),
            discount:discounts(id, name, amount),
            members:crew_members(
              id,
              role,
              derogation_validated,
              contact:contacts(id, first_name, last_name, email, phone, birth_date)
            )
          `
              )
              .eq('id', id)
              .single()) as any,
          'useCrewActions.getCrewById'
        );

        if (error) throw error;
        if (!data) return null;

        const crew = data as any;

        // Transformer la structure - inclure le rôle dans chaque contact
        const transformedCrew: Crew = {
          ...crew,
          members:
            crew.members
              ?.map((m: any) => ({
                ...m.contact,
                role: m.role,
                derogation_validated: m.derogation_validated ?? false,
              }))
              .filter(Boolean) || [],
          assigned_user: null,
        };

        // Enrichir avec l'utilisateur assigné si nécessaire
        // Utilise le cache du UsersContext (pas de requête réseau)
        if (transformedCrew.assigned_to) {
          const assignedUser = usersMap[transformedCrew.assigned_to];
          if (assignedUser) {
            transformedCrew.assigned_user = assignedUser;
          }
        }

        return transformedCrew;
      } catch (err) {
        console.error('[useCrewActions] getCrewById error:', err);
        return null;
      }
    },
    [supabase, usersMap]
  );

  /**
   * Mettre à jour un crew
   */
  const updateCrew = useCallback(
    async (id: string, data: Partial<Crew>): Promise<UpdateCrewResult> => {
      try {
        // 1. Récupérer les données actuelles pour le logging
        const oldCrew = await getCrewById(id);
        if (!oldCrew) {
          console.error('[useCrewActions] updateCrew: crew not found');
          return 'failed';
        }

        // 2. Nettoyer les chaînes vides pour les UUID optionnels
        const cleaned: any = { ...data };
        if (cleaned.product_id === '') cleaned.product_id = null;
        if (cleaned.vehicle_id === '') cleaned.vehicle_id = null;
        if (cleaned.association_id === '') cleaned.association_id = null;
        if (cleaned.discount_id === '') cleaned.discount_id = null;
        if (cleaned.assigned_to === '') cleaned.assigned_to = null;

        // 3. Dérogation : cascade sur derogation_validated UNIQUEMENT pour les
        //    membres concernés (>28 ans à la date de l'événement). Cibler tous
        //    les membres polluerait les <=28 ans (revue 2026-05-31, finding #5).
        //    Ne déclencher la cascade que si has_derogation a RÉELLEMENT changé :
        //    CrewInfoForm renvoie toujours has_derogation, donc un simple test
        //    `!== undefined` ré-écraserait les validations individuelles des
        //    membres à chaque sauvegarde d'un champ sans rapport (ex: budget) —
        //    cas état mixte A validé / B en attente (revue 2026-05-31, finding #5bis).
        const derogationChanged =
          cleaned.has_derogation !== undefined && cleaned.has_derogation !== oldCrew.has_derogation;
        const eventDate = oldCrew.product?.start_date ?? null;
        const over28ContactIds = (oldCrew.members ?? [])
          .filter((m) => isOver28AtEventDate(m.birth_date, eventDate))
          .map((m) => m.id);
        // Avec des membres concernés, les flags de l'équipage découlent de leurs
        // validations (triggers 00322) : has_derogation n'est jamais écrit à la
        // main, sinon un formulaire ouvert avant une validation l'écraserait.
        const crewUpdate = { ...cleaned };
        if (over28ContactIds.length > 0) delete crewUpdate.has_derogation;

        // 4. Faire l'update
        const { error } = await monitorQuery(
          'crews',
          'update',
          `id=${id}`,
          (() => supabase.from('crews').update(crewUpdate).eq('id', id)) as any,
          'useCrewActions.updateCrew'
        );

        if (error) {
          console.error('[useCrewActions] updateCrew error:', error);
          return 'failed';
        }

        // Puis les membres : un échec de l'équipage (ex. numéro déjà pris) ne
        // doit pas laisser une dérogation validée sans trace.
        let derogationSaved = true;
        if (derogationChanged && over28ContactIds.length > 0) {
          const { error: membersError } = await supabase
            .from('crew_members')
            .update({ derogation_validated: cleaned.has_derogation })
            .eq('crew_id', id)
            .in('contact_id', over28ContactIds);
          if (membersError) {
            console.error('[useCrewActions] updateCrew derogation error:', membersError);
            derogationSaved = false;
          }
        }

        // 5. Logger les changements réellement enregistrés (similaire à useCRUD)
        const saved = derogationSaved ? cleaned : crewUpdate;
        const newData = { ...oldCrew, ...saved };
        const changedFields = Object.keys(saved).filter((key) => key !== 'assigned_to');

        if (changedFields.length > 0) {
          await logEntityUpdate(supabase, 'crew', id, oldCrew, newData, undefined, changedFields);
        }

        return derogationSaved ? 'saved' : 'partial';
      } catch (err) {
        console.error('[useCrewActions] updateCrew error:', err);
        return 'failed';
      }
    },
    [supabase, getCrewById]
  );

  /**
   * Supprimer un crew archivé via RPC SECURITY DEFINER.
   * Les tables payment_attempts et bed_tokens étant SELECT-only pour les
   * utilisateurs authentifiés (RLS migration 00065), la suppression doit
   * passer par la RPC delete_archived_crew qui bypass RLS côté base.
   *
   * Autorisation vérifiée côté RPC (rôle admin/superadmin, superadmin requis si
   * paiements encaissés). Lève une erreur dont `message` porte le code métier
   * (crew_delete_forbidden / crew_has_paid_payments / crew_not_archived /
   * authentication_required) pour permettre un toast ciblé côté UI. Retourne
   * `true` en cas de succès (contrat attendu par le framework de liste).
   */
  const deleteCrew = useCallback(
    async (id: string): Promise<boolean> => {
      const { error } = await supabase.rpc('delete_archived_crew', {
        p_crew_id: id,
      });

      if (error) {
        console.error('[useCrewActions] deleteCrew RPC error:', error);
        throw new Error(error.message);
      }

      return true;
    },
    [supabase]
  );

  /**
   * Retirer l'assignation d'un utilisateur
   */
  const unassignUser = useCallback(
    async (crewId: string): Promise<boolean> => {
      try {
        const { error } = await monitorQuery(
          'crews',
          'update',
          `id=${crewId} (unassign)`,
          (() => supabase.from('crews').update({ assigned_to: null }).eq('id', crewId)) as any,
          'useCrewActions.unassignUser'
        );

        return !error;
      } catch (err) {
        console.error('[useCrewActions] unassignUser error:', err);
        return false;
      }
    },
    [supabase]
  );

  /**
   * Ajouter un membre à un équipage
   * Envoie un email de confirmation au contact ajouté
   * @param crewId - ID de l'équipage
   * @param contactId - ID du contact à ajouter
   * @param role - Rôle optionnel (pilot ou copilot)
   */
  const addCrewMember = useCallback(
    async (crewId: string, contactId: string, role?: CrewMemberRole): Promise<boolean> => {
      try {
        // R2 : idempotence — vérifier d'abord si le contact est DÉJÀ membre.
        // Sans ce probe, l'INSERT déclenche une violation unique (code 23505)
        // sur la contrainte `UNIQUE(crew_id, contact_id)` (migration 00001).
        // Cas reproductible : opération remove précédente échouée silencieusement
        // (RLS denial, race condition, network) → state local React invalidé
        // → tentative re-add du même contact → unique violation → addCrewMember
        // retournait false silencieusement, l'utilisateur voyait « rien ne se passe »
        // (commentaire client : « des fois (mais pas toujours), il nous est
        // impossible de mettre le nouveau contact sur l'équipage »).
        const { data: existing, error: probeError } = await supabase
          .from('crew_members')
          .select('id, role')
          .eq('crew_id', crewId)
          .eq('contact_id', contactId)
          .maybeSingle();

        if (probeError) {
          console.error('[useCrewActions] addCrewMember probe error:', probeError);
          return false;
        }

        if (existing) {
          // Déjà membre : si le rôle souhaité diffère, mettre à jour ; sinon no-op.
          if (role !== undefined && existing.role !== (role || null)) {
            const { error: roleError } = await supabase
              .from('crew_members')
              .update({ role: role || null })
              .eq('id', existing.id);
            if (roleError) {
              console.error('[useCrewActions] addCrewMember role update error:', roleError);
              return false;
            }
          }
          // Considéré comme succès — le contact est bien membre avec le bon rôle.
          return true;
        }

        const { error } = await monitorQuery(
          'crew_members',
          'insert',
          `crew=${crewId}, contact=${contactId}, role=${role || 'null'}`,
          (() =>
            supabase.from('crew_members').insert({
              crew_id: crewId,
              contact_id: contactId,
              role: role || null,
            })) as any,
          'useCrewActions.addCrewMember'
        );

        if (error) {
          // R2 : surfacer l'erreur pour diagnostic au lieu d'avaler silencieusement.
          // Le code Postgres '23505' = unique_violation (race condition possible si
          // 2 requêtes ADD simultanées passent toutes deux la probe ci-dessus).
          console.error(
            `[useCrewActions] addCrewMember insert error (code=${(error as any).code}, message=${error.message})`
          );
          return false;
        }

        // Lier les fichiers BPI du contact à l'équipage (Feature 2.3)
        try {
          // Chercher les fichiers BPI du contact
          const { data: bpiFiles } = await supabase
            .from('files')
            .select('*')
            .eq('entity_type', 'contact')
            .eq('entity_id', contactId)
            .like('file_path', 'bpi/%');

          if (bpiFiles && bpiFiles.length > 0) {
            for (const file of bpiFiles) {
              // Vérifier que l'entrée n'existe pas déjà pour cet équipage
              const { data: existing } = await supabase
                .from('files')
                .select('id')
                .eq('entity_type', 'crew')
                .eq('entity_id', crewId)
                .eq('file_path', file.file_path)
                .maybeSingle();

              if (!existing) {
                await supabase.from('files').insert({
                  entity_type: 'crew',
                  entity_id: crewId,
                  name: file.name,
                  file_path: file.file_path,
                  file_size: file.file_size,
                  mime_type: file.mime_type,
                  uploaded_by: user?.id || null,
                });
              }
            }
          }
        } catch (linkError) {
          console.warn('[useCrewActions] Erreur liaison fichiers BPI:', linkError);
          // Ne pas faire échouer l'ajout si la liaison échoue
        }

        // Récupérer les informations du crew (avec product et members) et du contact
        let crew: Crew | null = null;
        let contact: { id: string; first_name: string; last_name: string; email: string } | null =
          null;

        try {
          crew = await getCrewById(crewId);
          const { data: contactData } = await supabase
            .from('contacts')
            .select('id, first_name, last_name, email')
            .eq('id', contactId)
            .single();
          contact = contactData;
        } catch (fetchError) {
          console.error('[useCrewActions] addCrewMember fetch error:', fetchError);
        }

        // Logger l'ajout dans les deux timelines (crew + contact)
        if (crew && contact) {
          try {
            const contactName = `${contact.first_name} ${contact.last_name}`;
            const crewDisplayName =
              crew.name ||
              (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom');
            // Passer le userId pour respecter la politique RLS
            await logCrewMemberAdded(
              supabase,
              crewId,
              crewDisplayName,
              contactId,
              contactName,
              user?.id || null
            );
          } catch (logError) {
            console.error('[useCrewActions] addCrewMember logging error:', logError);
            // Ne pas faire échouer l'ajout si le logging échoue
          }

          // Envoyer l'email de confirmation au nouveau membre
          // Uniquement si le crew a un produit associé (avec dates) et un numéro d'équipage
          if (crew.product && crew.crew_number && contact.email) {
            try {
              // Préparer la liste des membres pour l'email
              const members =
                crew.members?.map((m) => ({
                  first_name: m.first_name,
                  last_name: m.last_name,
                })) || [];

              // Appeler l'edge function de manière asynchrone (ne pas bloquer)
              const product = crew.product as {
                id: string;
                name: string;
                start_date?: string;
                end_date?: string;
              };

              // Vérifier que les dates du produit sont disponibles
              if (product.start_date && product.end_date) {
                supabase.functions
                  .invoke('send-crew-confirmation-email', {
                    body: {
                      contact: {
                        id: contact.id,
                        email: contact.email,
                        first_name: contact.first_name,
                        last_name: contact.last_name,
                      },
                      crew: {
                        id: crew.id,
                        crew_number: crew.crew_number,
                        members,
                      },
                      product: {
                        name: product.name,
                        start_date: product.start_date,
                        end_date: product.end_date,
                      },
                    },
                  })
                  .then((result) => {
                    if (result.error) {
                      console.warn('[useCrewActions] Email confirmation non envoyé:', result.error);
                    } else {
                      console.warn(
                        `[useCrewActions] Email confirmation envoyé à ${contact?.email}`
                      );
                    }
                  })
                  .catch((emailError) => {
                    console.warn('[useCrewActions] Erreur envoi email confirmation:', emailError);
                  });
              } else {
                console.warn(
                  '[useCrewActions] Email non envoyé: dates du produit manquantes',
                  product
                );
              }
            } catch (emailError) {
              console.warn('[useCrewActions] Erreur préparation email confirmation:', emailError);
              // Ne pas faire échouer l'ajout si l'envoi d'email échoue
            }
          } else {
            console.warn(
              '[useCrewActions] Email non envoyé: produit, numéro équipage ou email manquant',
              {
                hasProduct: !!crew.product,
                crewNumber: crew.crew_number,
                hasEmail: !!contact.email,
              }
            );
          }
        } else {
          console.warn('[useCrewActions] Could not log or send email: crew or contact not found', {
            crew,
            contact,
          });
        }

        return true;
      } catch (err) {
        console.error('[useCrewActions] addCrewMember error:', err);
        return false;
      }
    },
    [supabase, getCrewById, user]
  );

  /**
   * Mettre à jour le rôle d'un membre d'équipage
   * @param crewId - ID de l'équipage
   * @param contactId - ID du contact
   * @param role - Nouveau rôle (pilot, copilot, ou null)
   */
  const updateCrewMemberRole = useCallback(
    async (crewId: string, contactId: string, role: CrewMemberRole | null): Promise<boolean> => {
      try {
        const { error } = await monitorQuery(
          'crew_members',
          'update',
          `crew=${crewId}, contact=${contactId}, role=${role}`,
          (() =>
            supabase
              .from('crew_members')
              .update({ role })
              .eq('crew_id', crewId)
              .eq('contact_id', contactId)) as any,
          'useCrewActions.updateCrewMemberRole'
        );

        if (error) {
          console.error('[useCrewActions] updateCrewMemberRole error:', error);
          return false;
        }

        return true;
      } catch (err) {
        console.error('[useCrewActions] updateCrewMemberRole error:', err);
        return false;
      }
    },
    [supabase]
  );

  /**
   * Retirer un membre d'un équipage
   */
  const removeCrewMember = useCallback(
    async (crewId: string, contactId: string): Promise<boolean> => {
      try {
        // Récupérer les informations AVANT la suppression pour le logging
        let crewName = '';
        let contactName = '';
        try {
          const crew = await getCrewById(crewId);
          const { data: contact } = await supabase
            .from('contacts')
            .select('first_name, last_name')
            .eq('id', contactId)
            .single();

          if (crew && contact) {
            crewName =
              crew.name ||
              (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom');
            contactName = `${contact.first_name} ${contact.last_name}`;
          }
        } catch (fetchError) {
          console.error('[useCrewActions] removeCrewMember fetch error:', fetchError);
        }

        // Effectuer la suppression — .select('id') pour détecter 0 lignes supprimées
        const { data: deletedRows, error } = await monitorQuery<{ id: string }[]>(
          'crew_members',
          'delete',
          `crew=${crewId}, contact=${contactId}`,
          (() =>
            supabase
              .from('crew_members')
              .delete()
              .eq('crew_id', crewId)
              .eq('contact_id', contactId)
              .select('id')) as any,
          'useCrewActions.removeCrewMember'
        );

        if (error) {
          // R2 : surfacer le code d'erreur PG (RLS denial = '42501', etc.)
          // au lieu d'avaler silencieusement. Critique pour diagnostiquer le
          // bug R2 reproduction (« des fois mais pas toujours impossible »).
          console.error(
            `[useCrewActions] removeCrewMember error (code=${(error as any).code}, message=${error.message})`
          );
          return false;
        }

        // Détecter une suppression silencieuse (0 lignes affectées — peut arriver si RLS ne correspond pas)
        const deletedCount = Array.isArray(deletedRows) ? deletedRows.length : 0;
        if (deletedCount === 0) {
          console.error(
            `[useCrewActions] removeCrewMember: DELETE returned 0 rows — crew_id=${crewId}, contact_id=${contactId}`
          );
          return false;
        }

        // Logger le retrait dans les deux timelines (crew + contact)
        if (crewName && contactName) {
          try {
            // Passer le userId pour respecter la politique RLS
            await logCrewMemberRemoved(
              supabase,
              crewId,
              crewName,
              contactId,
              contactName,
              user?.id || null
            );
          } catch (logError) {
            console.error('[useCrewActions] removeCrewMember logging error:', logError);
            // Ne pas faire échouer le retrait si le logging échoue
          }
        } else {
          console.warn('[useCrewActions] Could not log removal: crew or contact name missing', {
            crewName,
            contactName,
          });
        }

        return true;
      } catch (err) {
        console.error('[useCrewActions] removeCrewMember error:', err);
        return false;
      }
    },
    [supabase, getCrewById, user]
  );

  const updateDerogationValidated = useCallback(
    async (crewId: string, contactId: string, validated: boolean): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from('crew_members')
          .update({ derogation_validated: validated })
          .eq('crew_id', crewId)
          .eq('contact_id', contactId);

        if (error) {
          console.error('[useCrewActions] updateDerogationValidated error:', error);
          return false;
        }

        return true;
      } catch (err) {
        console.error('[useCrewActions] updateDerogationValidated error:', err);
        return false;
      }
    },
    [supabase]
  );

  return {
    getCrewById,
    updateCrew,
    deleteCrew,
    unassignUser,
    addCrewMember,
    updateCrewMemberRole,
    removeCrewMember,
    updateDerogationValidated,
  };
}
